import { FileText, Loader2, Pause, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";

const LEVEL_COLORS: Record<string, string> = {
  error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  warn: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  info: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  debug: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

function parseLogLine(line: string): { ts: string; level: string; source: string; msg: string } | null {
  // Structured log format: 2026-03-31T21:00:00.000+00:00 [subsystem] message
  const tsMatch = line.match(/^(\d{4}-\d{2}-\d{2}T[\d:.+Z-]+)\s+/);
  const ts = tsMatch?.[1] ?? "";
  const rest = tsMatch ? line.slice(tsMatch[0].length) : line;

  const subMatch = rest.match(/^\[([^\]]+)\]\s*/);
  const source = subMatch?.[1] ?? "";
  const msg = subMatch ? rest.slice(subMatch[0].length) : rest;

  const level = msg.includes("error") || msg.includes("ERROR") || msg.includes("FATAL") ? "error"
    : msg.includes("warn") || msg.includes("⚠️") ? "warn"
    : msg.includes("debug") ? "debug" : "info";

  return { ts: ts.slice(11, 19), level, source, msg: msg.trim() };
}

export function LogViewer() {
  const { rpc, state } = useGatewayRpc();
  const [lines, setLines] = useState<string[]>([]);
  const [paused, setPaused] = useState(false);
  const [levelFilter, setLevelFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<number>(0);

  const fetchLogs = useCallback(async () => {
    if (paused || state !== "connected") { return; }
    try {
      const res = await rpc<{ lines: string[]; cursor: number }>("logs.tail", {
        lines: 300,
        cursor: cursorRef.current,
      });
      if (res.lines.length > 0) {
        setLines((prev) => [...prev.slice(-2000), ...res.lines]); // keep last 2000 lines
        cursorRef.current = res.cursor;
      }
    } catch {
      // logs.tail may not be available
    }
  }, [rpc, state, paused]);

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 3000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  useEffect(() => {
    if (!paused && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, paused]);

  const parsed = lines.map(parseLogLine).filter(Boolean) as { ts: string; level: string; source: string; msg: string }[];
  const filtered = parsed.filter((l) => {
    if (levelFilter && l.level !== levelFilter) { return false; }
    if (sourceFilter && !l.source.toLowerCase().includes(sourceFilter.toLowerCase()) && !l.msg.toLowerCase().includes(sourceFilter.toLowerCase())) { return false; }
    return true;
  });

  if (state !== "connected") {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Log Viewer</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Log Viewer</h1>
        <div className="flex items-center gap-2">
          <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900">
            <option value="">All levels</option>
            <option value="error">Error</option>
            <option value="warn">Warn</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
          <input value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} placeholder="Filter..."
            className="w-32 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs outline-none dark:border-slate-700 dark:bg-slate-900" />
          <button type="button" onClick={() => setPaused(!paused)}
            className="rounded-md border border-slate-200 p-1.5 text-xs dark:border-slate-700">
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
          <span className="text-[10px] text-muted-foreground">{filtered.length} entries</span>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-950 p-2 dark:border-slate-800">
        {filtered.length === 0 ? (
          <div className="flex items-center gap-2 py-8 text-center text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Waiting for log entries...
          </div>
        ) : (
          <div className="space-y-0.5 font-mono text-[11px]">
            {filtered.map((l, i) => (
              <div key={i} className={`flex gap-2 rounded px-1.5 py-0.5 ${l.level === "error" ? "bg-red-950/50" : l.level === "warn" ? "bg-yellow-950/30" : ""}`}>
                <span className="shrink-0 text-slate-600">{l.ts}</span>
                <span className={`inline-flex w-10 shrink-0 items-center justify-center rounded px-1 text-[9px] font-medium ${LEVEL_COLORS[l.level] ?? ""}`}>{l.level}</span>
                {l.source && <span className="shrink-0 text-slate-500">[{l.source}]</span>}
                <span className={`min-w-0 break-all ${l.level === "error" ? "text-red-400" : "text-slate-300"}`}>{l.msg}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
